import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, Search, ChevronDown, ArrowRight, Printer, FileText, Pencil } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { passengersApi } from '@/api/passengers';
import { groupsApi } from '@/api/groups';
import { DataTable } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { PassengerForm } from '@/components/passengers/PassengerForm';
import { BulkImport } from '@/components/passengers/BulkImport';
import { BulkImportDocumentsWrapper } from '@/components/passengers/BulkImportDocumentsWrapper';
import { PassengerDocsPanel } from '@/components/passengers/PassengerDocsPanel';
import { BadgePrintView } from '@/components/passengers/BadgePrintView';
const STATUS_OPTIONS = [
    { value: '', label: 'Todos los estados' },
    { value: 'CONFIRMED', label: 'Confirmado' },
    { value: 'WAITLIST', label: 'Lista de espera' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'NO_SHOW', label: 'No presentó' },
];
export function PasajerosPage() {
    const queryClient = useQueryClient();
    const tenantSlug = useAuthStore((s) => s.tenant?.slug);
    const accessToken = useAuthStore((s) => s.accessToken);
    const [groupFilter, setGroupFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showBulkDocsModal, setShowBulkDocsModal] = useState(false);
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [passengerToEdit, setPassengerToEdit] = useState(null);
    const [passengerToDelete, setPassengerToDelete] = useState(null);
    const [passengerToChangeStatus, setPassengerToChangeStatus] = useState(null);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [badgePrintPassengers, setBadgePrintPassengers] = useState(null);
    const [passengerToDocs, setPassengerToDocs] = useState(null);
    const queryParams = {
        ...(groupFilter ? { groupId: groupFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(searchQuery ? { search: searchQuery } : {}),
    };
    const { data: groupsData } = useQuery({
        queryKey: ['groups', 'active'],
        queryFn: () => groupsApi.getGroups({ pageSize: 100 }),
    });
    const allGroups = groupsData?.data ?? [];
    // Show all groups in selector but visually mark concluded ones
    const groups = allGroups;
    const { data: passengersData, isLoading } = useQuery({
        queryKey: ['passengers', queryParams],
        queryFn: () => passengersApi.getPassengers(queryParams),
    });
    const passengers = passengersData?.data ?? [];
    const createMutation = useMutation({
        mutationFn: (dto) => passengersApi.createPassenger(dto),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['passengers'] });
            setShowCreateModal(false);
        },
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, dto }) => passengersApi.updatePassenger(id, dto),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['passengers'] });
            setPassengerToEdit(null);
        },
    });
    const deleteMutation = useMutation({
        mutationFn: (id) => passengersApi.deletePassenger(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['passengers'] });
            setShowDeleteModal(false);
            setPassengerToDelete(null);
        },
    });
    const statusMutation = useMutation({
        mutationFn: ({ id, status }) => passengersApi.updateStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['passengers'] });
            setPassengerToChangeStatus(null);
            setStatusDropdownOpen(false);
        },
    });
    const columns = [
        {
            key: 'name',
            label: 'Nombre',
            render: (p) => (_jsxs("div", { children: [_jsxs("p", { className: "text-sm font-medium text-[var(--color-text)]", children: [p.firstName, " ", p.lastName] }), p.email && _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: p.email })] })),
        },
        { key: 'phone', label: 'Teléfono', render: (p) => _jsx("span", { children: p.phone ?? '—' }) },
        {
            key: 'document',
            label: 'Documento',
            render: (p) => (_jsxs("div", { children: [_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: p.documentType ?? '—' }), _jsx("p", { className: "text-xs font-mono", children: p.documentNumber ?? '—' })] })),
        },
        {
            key: 'seat',
            label: 'Asiento',
            render: (p) => _jsx("span", { className: "font-mono text-xs", children: p.seatNumber ?? '—' }),
        },
        {
            key: 'room',
            label: 'Hab.',
            render: (p) => _jsx("span", { className: "font-mono text-xs", children: p.roomNumber ?? '—' }),
        },
        {
            key: 'status',
            label: 'Estado',
            render: (p) => {
                const currentStatus = STATUS_OPTIONS.find((s) => s.value === p.status);
                return (_jsx("div", { className: "relative inline-block", children: _jsxs("button", { className: "flex items-center gap-1 text-sm hover:opacity-80 transition-opacity", onClick: (e) => {
                            e.stopPropagation();
                            setPassengerToChangeStatus(p);
                            setStatusDropdownOpen((prev) => !prev);
                        }, children: [_jsx("span", { className: `w-2 h-2 rounded-full`, style: {
                                    backgroundColor: p.status === 'CONFIRMED'
                                        ? 'var(--color-success)'
                                        : p.status === 'WAITLIST'
                                            ? 'var(--color-warning)'
                                            : p.status === 'CANCELLED' || p.status === 'NO_SHOW'
                                                ? 'var(--color-error)'
                                                : 'var(--color-text-muted)',
                                } }), currentStatus?.label ?? p.status, _jsx(ChevronDown, { className: "w-3 h-3" })] }) }));
            },
        },
        {
            key: 'portal',
            label: 'Portal',
            render: (p) => {
                if (!tenantSlug || !p.email || !accessToken)
                    return _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u2014" });
                return (_jsx("button", { title: "Abrir portal del pasajero", className: "inline-flex items-center gap-1 text-sm text-[var(--tenant-primary)] hover:opacity-80 transition-opacity", onClick: (e) => {
                        e.stopPropagation();
                        localStorage.setItem('gv-portal-admin-bypass', JSON.stringify({ tenantSlug, passengerId: p.id, accessToken }));
                        window.open(`/portal/${tenantSlug}`, '_blank');
                    }, children: _jsx(ArrowRight, { className: "w-4 h-4" }) }));
            },
        },
        {
            key: 'badge',
            label: '',
            render: (p) => (_jsx("button", { title: "Imprimir badge QR", className: "inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--tenant-primary)] transition-colors", onClick: (e) => { e.stopPropagation(); setBadgePrintPassengers([p]); }, children: _jsx(Printer, { className: "w-4 h-4" }) })),
        },
        {
            key: 'docs',
            label: '',
            render: (p) => (_jsx("button", { title: "Ver documentos", className: "inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--tenant-primary)] transition-colors", onClick: (e) => {
                    e.stopPropagation();
                    setPassengerToDocs(p);
                    setShowDocsModal(true);
                }, children: _jsx(FileText, { className: "w-4 h-4" }) })),
        },
        {
            key: 'edit',
            label: '',
            render: (p) => (_jsx("button", { title: "Editar pasajero", className: "inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--tenant-primary)] transition-colors", onClick: (e) => {
                    e.stopPropagation();
                    setPassengerToEdit(p);
                }, children: _jsx(Pencil, { className: "w-4 h-4" }) })),
        },
    ];
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [_jsx(PageHeader, { title: "Pasajeros", subtitle: "Registro y gesti\u00F3n de pasajeros", actions: _jsxs("div", { className: "flex items-center gap-2", children: [passengers.length > 0 && (_jsxs("button", { className: "btn btn-secondary btn-md flex items-center gap-2", onClick: () => setBadgePrintPassengers(passengers), title: "Imprimir badges QR de todos los pasajeros visibles", children: [_jsx(Printer, { className: "w-4 h-4" }), "Imprimir badges"] })), _jsxs("button", { className: "btn-secondary btn-md flex items-center gap-2", onClick: () => setShowBulkDocsModal(true), children: [_jsx(FileText, { className: "w-4 h-4" }), "Importar docs URL"] }), _jsxs("button", { className: "btn-secondary btn-md flex items-center gap-2", onClick: () => setShowBulkModal(true), children: [_jsx(Upload, { className: "w-4 h-4" }), "Importar CSV"] }), _jsxs("button", { className: "btn-primary btn-md flex items-center gap-2", onClick: () => setShowCreateModal(true), children: [_jsx(Plus, { className: "w-4 h-4" }), "Agregar pasajero"] })] }) }), _jsxs("div", { className: "flex flex-col sm:flex-row gap-3", children: [_jsxs("div", { className: "relative flex-1", children: [_jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" }), _jsx("input", { type: "text", className: "input pl-9", placeholder: "Buscar por nombre, email o documento...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] }), _jsx(Select, { value: groupFilter, options: [
                            { value: '', label: 'Todos los grupos' },
                            ...groups.map((g) => ({
                                value: g.id,
                                label: g.status === 'COMPLETED' ? `✓ ${g.name}` : g.status === 'CANCELLED' ? `✗ ${g.name}` : g.name,
                            })),
                        ], onChange: (v) => setGroupFilter(v), className: "w-full sm:w-56" }), _jsx(Select, { value: statusFilter, options: STATUS_OPTIONS, onChange: (v) => setStatusFilter(v), className: "w-full sm:w-44" })] }), _jsx(DataTable, { columns: columns, data: passengers, isLoading: isLoading, rowKey: (p) => p.id, emptyMessage: "No hay pasajeros registrados" }), _jsx(Modal, { isOpen: showCreateModal || !!passengerToEdit, onClose: () => {
                    setShowCreateModal(false);
                    setPassengerToEdit(null);
                }, title: passengerToEdit ? 'Editar pasajero' : 'Agregar pasajero', size: "lg", children: _jsx(PassengerForm, { passenger: passengerToEdit ?? undefined, groupId: groupFilter, onSubmit: (dto) => {
                        if (passengerToEdit) {
                            updateMutation.mutate({ id: passengerToEdit.id, dto: dto });
                        }
                        else {
                            createMutation.mutate(dto);
                        }
                    }, onCancel: () => {
                        setShowCreateModal(false);
                        setPassengerToEdit(null);
                    }, isLoading: createMutation.isPending || updateMutation.isPending }) }), _jsx(Modal, { isOpen: showBulkModal, onClose: () => setShowBulkModal(false), title: "Importar pasajeros desde CSV", size: "lg", children: _jsx(BulkImport, { groupId: groupFilter, onComplete: () => {
                        setShowBulkModal(false);
                        queryClient.invalidateQueries({ queryKey: ['passengers'] });
                    } }) }), _jsx(Modal, { isOpen: showBulkDocsModal, onClose: () => setShowBulkDocsModal(false), title: "Importar documentos URL desde CSV", size: "lg", children: _jsx(BulkImportDocumentsWrapper, { onComplete: () => {
                        setShowBulkDocsModal(false);
                    } }) }), _jsx(Modal, { isOpen: showDocsModal, onClose: () => { setShowDocsModal(false); setPassengerToDocs(null); }, title: `Documentos — ${passengerToDocs?.firstName} ${passengerToDocs?.lastName}`, size: "lg", children: passengerToDocs && (_jsx(PassengerDocsPanel, { passengerId: passengerToDocs.id })) }), _jsx(Modal, { isOpen: showDeleteModal, onClose: () => {
                    setShowDeleteModal(false);
                    setPassengerToDelete(null);
                }, title: "Eliminar pasajero", size: "sm", footer: _jsxs(_Fragment, { children: [_jsx("button", { className: "btn-secondary btn-md", onClick: () => setShowDeleteModal(false), children: "Cancelar" }), _jsx("button", { className: "btn-danger btn-md", onClick: () => passengerToDelete && deleteMutation.mutate(passengerToDelete.id), disabled: deleteMutation.isPending, children: deleteMutation.isPending ? 'Eliminando...' : 'Eliminar' })] }), children: passengerToDelete && (_jsxs("p", { className: "text-sm text-[var(--color-text)]", children: ["\u00BFEst\u00E1s seguro de eliminar a", ' ', _jsxs("strong", { children: [passengerToDelete.firstName, " ", passengerToDelete.lastName] }), "? Esta acci\u00F3n no se puede deshacer."] })) }), badgePrintPassengers && (_jsx(BadgePrintView, { passengers: badgePrintPassengers, groupName: (() => {
                    const gid = groupFilter || badgePrintPassengers[0]?.groupId;
                    const g = groups.find((g) => g.id === gid);
                    if (!g)
                        return undefined;
                    const label = g.code ? `${g.name} (${g.code})` : g.name;
                    return label.length > 25 ? label.slice(0, 25) + '…' : label;
                })(), onClose: () => setBadgePrintPassengers(null) }))] }));
}
//# sourceMappingURL=PasajerosPage.js.map